import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AvForm } from "availity-reactstrap-validation";
import { useSnapshot } from "valtio";

import { CustomCardBody } from "@components/Common/CustomCardStyled";
import {
    store as InformationStore,
    setIsActionsClicked,
    setActions,
    setIsEdited,
} from "../../store";
import {
    LANGUAGES,
    TRANSLATION_MODE_INFORMATION,
} from "../../../../../constants/index";

import { useInformationForm } from "./useInformationForm";
import InformationFormHeader from "./components/InformationFormHeader";
import InformationModeField from "./components/InformationModeField";
import DisplayNameField from "./components/DisplayNameField";
import SupportOfSaleField from "./components/SupportOfSaleField";
import SalesMethodField from "./components/SalesMethodField";
import TagsField from "./components/TagsField";
import InformationFormFooter from "./components/InformationFormFooter";
import InformationFormModals from "./components/InformationFormModals";

import "@assets/swal.css";
import "@assets/theme.css";
import "react-toastify/dist/ReactToastify.css";
import "../index.css";

type ModeOfInformationFormType = {
    setIsAddModeOfInformationClicked: Function;
    isAddModeOfInformationClicked: boolean;
    setDataIsUpdated: React.Dispatch<React.SetStateAction<boolean | number>>;
    dataInformation: any;
    isAddNewModeOfInformationButtonClicked: boolean;
    setDataInformation: Function;
    inputInformationMode: boolean;
    setInputInformationMode: Function;
    dataIsUpdated?: boolean | number;
    dataTable?: any;
    setLimit: Function;
    isConsult: boolean;
    setIsConsult: Function;
};

export default function InformationForm(props: ModeOfInformationFormType) {
    const {
        setIsAddModeOfInformationClicked,
        isAddModeOfInformationClicked,
        dataInformation,
        setDataInformation,
        isConsult,
        setIsConsult,
        dataTable,
        setLimit,
    } = props;

    const { t } = useTranslation();
    const { isActionsClicked, isEdited } = useSnapshot(InformationStore);

    const {
        form,
        dispatch,
        informationModes,
        supportOfSales,
        modeOfSales,
        tags,
        language,
        isSendingTag,
        setIsSendingTag,
        isModalOpened,
        setIsModalOpened,
        isModalOpen,
        setIsModalOpen,
        isModalModifiedOpened,
        setIsModalModifiedOpened,
        isAdvancedDisplayNameModalOpen,
        setIsAdvancedDisplayNameModalOpen,
        dataEdited,
        associated,
        KIOSKData,
        handleValidate,
        clearAllSelections,
        i18nextLng,
        selectedLanguage,
    } = useInformationForm(props);

    const handleClose = useCallback(() => {
        setIsConsult(false);
        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(() => {
                setIsEdited(false);
                setDataInformation([]);
                setIsAddModeOfInformationClicked(
                    !isAddModeOfInformationClicked
                );
            });
        } else {
            setDataInformation([]);
            setIsAddModeOfInformationClicked(!isAddModeOfInformationClicked);
            setIsEdited(false);
        }
    }, [
        isEdited,
        isActionsClicked,
        isAddModeOfInformationClicked,
        setIsConsult,
        setDataInformation,
        setIsAddModeOfInformationClicked,
    ]);

    const handleCancel = useCallback(() => {
        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(() => {
                setIsAddModeOfInformationClicked(
                    !isAddModeOfInformationClicked
                );
                setIsEdited(false);
            });
        } else {
            setIsAddModeOfInformationClicked(!isAddModeOfInformationClicked);
            setIsEdited(false);
        }
        setIsConsult(false);
    }, [
        isEdited,
        isActionsClicked,
        isAddModeOfInformationClicked,
        setIsConsult,
        setIsAddModeOfInformationClicked,
    ]);

    const handleInformationModeChange = useCallback(
        (e: any) => {
            const label = e?.label ?? "";
            const value = e?.value ?? "";
            dispatch({
                type: "SET_FIELDS",
                fields: {
                    informationMode: label,
                    informationModeId: value,
                    inputInformationMode: false,
                    displayName: label.trim(),
                    selectName: false,
                },
            });
            setIsEdited(true);

            if (!e || (e === null && form.displayName === "")) {
                dispatch({
                    type: "SET_FIELD",
                    field: "advancedDisplayName",
                    value: {
                        languages: LANGUAGES,
                    },
                });
            } else {
                dispatch({
                    type: "SET_FIELD",
                    field: "advancedDisplayName",
                    value: {
                        ...form.advancedDisplayName,
                        languages: TRANSLATION_MODE_INFORMATION(t)[e.label],
                    },
                });
            }
        },
        [dispatch, form, t]
    );

    const handleDisplayNameChange = useCallback(
        (e: any) => {
            const val = e.target.value;
            dispatch({
                type: "SET_FIELDS",
                fields: { displayName: val, selectName: false },
            });
            setIsEdited(true);

            if (form.informationMode === "" && val === "") {
                dispatch({
                    type: "SET_FIELD",
                    field: "advancedDisplayName",
                    value: {
                        languages: LANGUAGES,
                    },
                });
            } else {
                dispatch({
                    type: "SET_FIELD",
                    field: "advancedDisplayName",
                    value: {
                        ...form.advancedDisplayName,
                        languages: {
                            ...form.advancedDisplayName.languages,
                            [selectedLanguage.value]: val,
                        },
                    },
                });
            }
        },
        [dispatch, form, selectedLanguage.value]
    );

    const handleSupportOfSaleChange = useCallback(
        (e: any) => {
            setIsEdited(true);
            dispatch({
                type: "SET_FIELDS",
                fields: {
                    inputSupportOfSale: false,
                    supportOfSale: e ?? {},
                    modeOfSale: [],
                },
            });
            clearAllSelections();
        },
        [dispatch, clearAllSelections]
    );

    const handleAdvancedClick = useCallback(() => {
        setIsAdvancedDisplayNameModalOpen(!isAdvancedDisplayNameModalOpen);
    }, [isAdvancedDisplayNameModalOpen, setIsAdvancedDisplayNameModalOpen]);

    const setModeOfSaleResult = useCallback(
        (val: any) =>
            dispatch({ type: "SET_FIELD", field: "modeOfSale", value: val }),
        [dispatch]
    );
    const handleModeOfSaleChange = useCallback(
        (e: any) => {
            setIsEdited(true);
            const current = form.modeOfSale;
            dispatch({
                type: "SET_FIELDS",
                fields: { inputModeOfSale: false, modeOfSale: [...current, e] },
            });
        },
        [dispatch, form]
    );
    const handleModeOfSaleDelete = useCallback(() => {
        setIsEdited(true);
        dispatch({ type: "SET_FIELD", field: "inputModeOfSale", value: false });
    }, [dispatch]);

    const setTagsDataResult = useCallback(
        (val: any) =>
            dispatch({ type: "SET_FIELD", field: "tagsData", value: val }),
        [dispatch]
    );
    const handleTagsChange = useCallback(() => setIsEdited(true), []);
    const handleTagsDelete = useCallback(() => setIsEdited(true), []);

    const setAdvancedDisplayName = useCallback(
        (val: any) =>
            dispatch({
                type: "SET_FIELD",
                field: "advancedDisplayName",
                value: val,
            }),
        [dispatch]
    );

    const isEditMode = dataInformation?.Id !== undefined;
    const headerTitle = isEditMode
        ? t(
              isConsult
                  ? "View a mode of information"
                  : "Modify a mode of information"
          )
        : t("Add a Mode of information");
    const advancedDisplayNameValue =
        form.advancedDisplayName?.languages?.[i18nextLng] ?? "";
    const isSupportEmpty =
        !form.supportOfSale || Object.keys(form.supportOfSale).length === 0;
    const isAdvancedDisabled =
        advancedDisplayNameValue === "" && form.informationMode === "";

    return (
        <React.Fragment>
            <AvForm className="card">
                <InformationFormHeader
                    formTitle={form.title}
                    headerTitle={headerTitle}
                    onClose={handleClose}
                />

                <CustomCardBody
                    className="pl-3 pr-5"
                    style={{ maxHeight: "470px", overflowY: "auto" }}
                >
                    <InformationModeField
                        informationMode={form.informationMode}
                        informationModes={informationModes}
                        isEditMode={isEditMode}
                        isConsult={isConsult}
                        inputInformationMode={form.inputInformationMode}
                        isNameExist={form.isNameExist}
                        onChange={handleInformationModeChange}
                    />

                    <DisplayNameField
                        displayNameValue={advancedDisplayNameValue}
                        selectName={form.selectName}
                        isConsult={isConsult}
                        isEditMode={isEditMode}
                        informationMode={form.informationMode}
                        isAdvancedDisabled={isAdvancedDisabled}
                        onChange={handleDisplayNameChange}
                        onAdvancedClick={handleAdvancedClick}
                    />

                    <SupportOfSaleField
                        supportOfSale={form.supportOfSale}
                        supportOfSales={supportOfSales}
                        isEditMode={isEditMode}
                        isConsult={isConsult}
                        inputSupportOfSale={form.inputSupportOfSale}
                        isNameExist={form.isNameExist}
                        onChange={handleSupportOfSaleChange}
                    />

                    <SalesMethodField
                        modeOfSale={form.modeOfSale}
                        modeOfSales={modeOfSales}
                        isConsult={isConsult}
                        inputModeOfSale={form.inputModeOfSale}
                        isSupportEmpty={isSupportEmpty}
                        setResult={setModeOfSaleResult}
                        onChange={handleModeOfSaleChange}
                        onDelete={handleModeOfSaleDelete}
                    />

                    <TagsField
                        tagsData={form.tagsData}
                        tags={tags}
                        isConsult={isConsult}
                        isSendingTag={isSendingTag}
                        setResult={setTagsDataResult}
                        onChange={handleTagsChange}
                        onDelete={handleTagsDelete}
                    />
                </CustomCardBody>

                <InformationFormFooter
                    isConsult={isConsult}
                    onCancel={handleCancel}
                    onValidate={handleValidate}
                />
            </AvForm>

            <InformationFormModals
                form={form}
                isModalOpened={isModalOpened}
                setIsModalOpened={setIsModalOpened}
                setIsAddClicked={setIsAddModeOfInformationClicked}
                setEditedData={setDataInformation}
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                setDataIsUpdated={props.setDataIsUpdated}
                dataModeInformation={dataEdited}
                associated={associated}
                setIsSendingTag={setIsSendingTag}
                dataTable={dataTable}
                setIsAddModeOfInformationClicked={
                    setIsAddModeOfInformationClicked
                }
                setLimit={setLimit}
                KIOSKData={KIOSKData}
                isModalModifiedOpened={isModalModifiedOpened}
                setIsModalModifiedOpened={setIsModalModifiedOpened}
                isAdvancedDisplayNameModalOpen={isAdvancedDisplayNameModalOpen}
                setIsAdvancedDisplayNameModalOpen={
                    setIsAdvancedDisplayNameModalOpen
                }
                setAdvancedDisplayName={setAdvancedDisplayName}
                language={language}
                isConsult={isConsult}
                isActionsClicked={isActionsClicked}
            />
        </React.Fragment>
    );
}
